import { useMemo, useState } from 'react';
import { validateFormSchema, STATIC_FIELD_TYPES, type FormSchema, type FormFieldDef } from '@hcc/types';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api';
import {
  usePublishFormVersion,
  type OnboardingFormSummary,
} from '../../../hooks/useOnboardingForms';
import { useBuilderAutosave } from './useBuilderAutosave';
import { Button } from '../../ui/Button';
import { PageEditor } from './PageEditor';
import { FormPreview } from './FormPreview';
import { FieldConfigModal } from './FieldConfigModal';
import { DEFAULT_CONSENT } from './fieldMeta';
import {
  emptyDraft,
  addPage,
  removePage,
  movePage,
  updatePage,
  addField,
  updateField,
  removeField,
  moveField,
  hasSpecialCategory,
} from './draftOps';
import { ArrowLeft, UploadCloud, AlertCircle, Plus } from 'lucide-react';

interface FormEditorProps {
  form: OnboardingFormSummary;
  onBack: () => void;
}

function latestVersion(form: OnboardingFormSummary) {
  return [...form.versions].sort((a, b) => b.version - a.version)[0] ?? null;
}

const inputClass =
  'mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm';

/** The form builder canvas + live preview for a single form. */
export function FormEditor({ form, onBack }: FormEditorProps) {
  const seed = useMemo(() => latestVersion(form), [form]);
  // Prefer an autosaved draft (unpublished work) over the latest published
  // version, so reopening a form restores exactly where you left off.
  const draft = form.draft;
  const [title, setTitle] = useState(draft?.title ?? seed?.title ?? form.title);
  const [description, setDescription] = useState(draft?.description ?? seed?.description ?? '');
  const [schema, setSchema] = useState<FormSchema>(draft?.schema ?? seed?.schema ?? emptyDraft());
  const [editing, setEditing] = useState<{ page: number; field: number } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  // Edit one page at a time so a long form doesn't become one huge scroll.
  const [activePage, setActivePage] = useState(0);
  const active = Math.min(activePage, schema.pages.length - 1);

  const publish = usePublishFormVersion();
  const special = hasSpecialCategory(schema);

  const autosave = useBuilderAutosave({
    formId: form.id,
    fallbackTitle: form.title,
    title,
    description,
    schema,
  });

  const editingField: FormFieldDef | null =
    editing ? schema.pages[editing.page]?.fields[editing.field] ?? null : null;

  // Carry type + options too so the condition editor can offer the driving
  // field's own answers as a pick list (not just a free-text value box).
  const otherFields = editing
    ? schema.pages
        .flatMap((p) => p.fields)
        .filter((f) => f.key !== editingField?.key)
        .map((f) => ({ key: f.key, label: f.label, type: f.type, options: f.options }))
    : [];

  // A page's visibility can only depend on a non-static field from an EARLIER
  // page (the inductee answers it before reaching this page).
  const earlierFields = schema.pages
    .slice(0, active)
    .flatMap((p) => p.fields)
    .filter((f) => !STATIC_FIELD_TYPES.includes(f.type))
    .map((f) => ({ key: f.key, label: f.label, type: f.type, options: f.options }));

  async function onPublish() {
    // Attach consent automatically when special-category fields are present.
    const toPublish: FormSchema = special
      ? { ...schema, consent: schema.consent ?? DEFAULT_CONSENT }
      : { ...schema, consent: undefined };
    const result = validateFormSchema(toPublish);
    if (!result.valid) {
      setErrors(result.errors);
      toast.error('Fix the highlighted problems before publishing');
      return;
    }
    setErrors([]);
    try {
      const res = await publish.mutateAsync({ id: form.id, title: title.trim(), description: description.trim() || undefined, schema: toPublish });
      autosave.markPublished();
      toast.success(`Published version ${res.version.version}`);
      onBack();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not publish');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" iconLeft={<ArrowLeft className="h-4 w-4" />} onClick={() => autosave.flushThen(onBack)}>
          Back to forms
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-fg-dim" aria-live="polite">
            {autosave.saveState === 'saving'
              ? 'Saving…'
              : autosave.saveState === 'saved'
                ? 'All changes saved'
                : autosave.saveState === 'error'
                  ? 'Could not save draft'
                  : 'Changes save automatically'}
          </span>
          <Button
            variant="secondary"
            size="sm"
            loading={autosave.savePending}
            onClick={async () => {
              const ok = await autosave.saveNow();
              if (ok) toast.success('Draft saved');
              else toast.error('Could not save draft');
            }}
          >
            Save draft
          </Button>
          <Button variant="primary" size="sm" iconLeft={<UploadCloud className="h-4 w-4" />} loading={publish.isPending} onClick={onPublish}>
            Publish version
          </Button>
        </div>
      </div>

      {errors.length > 0 ? (
        <div className="flex gap-2 rounded-token-sm border border-danger-soft/30 bg-danger-soft/15 p-3 text-sm text-danger-soft-fg">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <ul className="list-disc space-y-0.5 pl-4">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-token border border-line/10 bg-surface p-3">
            <label className="block text-sm">
              <span className="font-medium text-fg-muted">Form title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
            </label>
            <label className="mt-3 block text-sm">
              <span className="font-medium text-fg-muted">Description</span>
              <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} />
            </label>
          </div>

          {/* Page tabs: edit one page at a time so long forms stay manageable. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {schema.pages.map((p, pi) => (
              <button
                key={pi}
                type="button"
                onClick={() => setActivePage(pi)}
                className={
                  'rounded-token-sm px-2.5 py-1.5 text-xs ' +
                  (pi === active
                    ? 'bg-accent text-fg-on-accent'
                    : 'bg-surface-2 text-fg-muted hover:bg-surface-inset')
                }
              >
                {pi + 1}. {p.title || `Page ${pi + 1}`}
                <span className="ml-1 opacity-70">({p.fields.length})</span>
              </button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Plus className="h-4 w-4" />}
              onClick={() => {
                setSchema((s) => addPage(s));
                setActivePage(schema.pages.length);
              }}
            >
              Add page
            </Button>
          </div>

          <PageEditor
            key={active}
            page={schema.pages[active]!}
            pageIdx={active}
            totalPages={schema.pages.length}
            earlierFields={earlierFields}
            onUpdatePage={(patch) => setSchema((s) => updatePage(s, active, patch))}
            onRemovePage={() => {
              setSchema((s) => removePage(s, active));
              setActivePage((a) => Math.max(0, a - 1));
            }}
            onMovePage={(dir) => {
              setSchema((s) => movePage(s, active, dir));
              setActivePage((a) => Math.min(Math.max(0, a + dir), schema.pages.length - 1));
            }}
            onAddField={(type) => {
              // Jump straight into configuring the new field (it's appended
              // to the end of the active page).
              const newIdx = schema.pages[active]?.fields.length ?? 0;
              setSchema((s) => addField(s, active, type));
              setEditing({ page: active, field: newIdx });
            }}
            onEditField={(fi) => setEditing({ page: active, field: fi })}
            onRemoveField={(fi) => setSchema((s) => removeField(s, active, fi))}
            onMoveField={(fi, dir) => setSchema((s) => moveField(s, active, fi, dir))}
          />

          {special ? (
            <div className="rounded-token border border-warning-soft/30 bg-warning-soft/10 p-3">
              <div className="text-sm font-medium text-fg">Consent statement</div>
              <p className="mt-0.5 text-xs text-fg-dim">
                Shown before submit because this form collects special-category data. The inductee must agree.
              </p>
              <textarea
                rows={4}
                value={schema.consent?.statement ?? DEFAULT_CONSENT.statement}
                onChange={(e) => setSchema((s) => ({ ...s, consent: { required: true, statement: e.target.value } }))}
                className={inputClass}
              />
            </div>
          ) : null}
        </div>

        <div className="lg:sticky lg:top-4 lg:self-start">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-dim">Live preview</div>
          <FormPreview schema={schema} title={title} description={description} />
        </div>
      </div>

      {editing && editingField ? (
        <FieldConfigModal
          field={editingField}
          otherFields={otherFields}
          onSave={(next) => {
            setSchema((s) => updateField(s, editing.page, editing.field, next));
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
